import type { FiltersState } from '../../hooks/useFilters';
import {
  mergeAvitoScheduleMappings,
  mapHhEducationToAvitoEducation,
  mapHhTotalExperienceToAvitoExperience,
  mapHhWorkFormatToAvitoSchedule,
  mapHhWorkScheduleToAvitoSchedule,
  mapHhWorkingHoursToAvitoSchedule,
} from './filterMappings';

export function normalizeAvitoVacancyList(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.map(String).map((item) => item.trim()).filter(Boolean);
  }

  if (typeof value === 'string' && value.trim()) {
    return value.split(',').map((item) => item.trim()).filter(Boolean).map(String);
  }

  return [];
}

function firstFilledValue(...values: unknown[]): unknown {
  return values.find((value) => {
    if (Array.isArray(value)) return value.length > 0;
    if (typeof value === 'string') return value.trim() !== '';
    return value !== undefined && value !== null;
  });
}

function hasOwnValue(source: Record<string, unknown>, key: string): boolean {
  return Object.prototype.hasOwnProperty.call(source, key);
}

function normalizeSkillList(value: unknown): string[] {
  const rawList = Array.isArray(value) ? value : (value ? [value] : []);
  return rawList
    .flatMap((item) => {
      if (item && typeof item === 'object') {
        const objectValue = item as Record<string, unknown>;
        return objectValue.name ?? objectValue.title ?? objectValue.text ?? objectValue.value ?? '';
      }
      return item;
    })
    .flatMap((item) => String(item).split(/[,;\n]/))
    .map((item) => item.trim())
    .filter(Boolean);
}

function parseSkillGroups(value: unknown): Array<{ type?: unknown; skills?: unknown }> {
  if (Array.isArray(value)) {
    return value as Array<{ type?: unknown; skills?: unknown }>;
  }

  if (typeof value === 'string' && value.trim()) {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  return [];
}

function getRequiredSkillsFromSource(source: Partial<FiltersState> | Record<string, unknown>): string[] {
  const groups = parseSkillGroups((source as any).skillGroups);
  const groupedSkills = groups
    .filter((group) => isAndSkillGroup(group as Record<string, unknown>))
    .flatMap((group) => normalizeSkillList(group?.skills));

  return Array.from(new Set([
    ...groupedSkills,
    ...normalizeSkillList((source as any).skillsAll),
  ]));
}

function getAndSkillsFromSource(source: Partial<FiltersState> | Record<string, unknown>): string[] {
  const groups = parseSkillGroups((source as any).skillGroups);
  const requiredSkills = getRequiredSkillsFromSource(source);

  if (requiredSkills.length > 0) {
    return requiredSkills;
  }

  const optionalSkills = Array.from(new Set([
    ...groups
      .filter((group) => !isAndSkillGroup(group as Record<string, unknown>))
      .flatMap((group) => normalizeSkillList(group?.skills)),
    ...normalizeSkillList((source as any).skillsAny),
    ...normalizeSkillList((source as any).topics),
  ]));

  return optionalSkills.length === 1 ? optionalSkills : [];
}

function normalizeSkillGroupType(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.flatMap(normalizeSkillGroupType);
  }

  return String(value ?? '')
    .split(/[,;\s]+/)
    .map((item) => item.trim().toLowerCase())
    .filter(Boolean);
}

function isAndSkillGroup(group: Record<string, unknown>): boolean {
  if (group.required === true || group.mandatory === true) {
    return true;
  }

  return [
    group.type,
    group.logic,
    group.operator,
    group.condition,
    group.mode,
  ].some((value) => normalizeSkillGroupType(value).some((type) => type === 'all' || type === 'and'));
}

export function getAvitoAndSkillsFromSource(
  source: Partial<FiltersState> | Record<string, unknown>,
): string[] {
  return getAndSkillsFromSource(source);
}

function stringifyBoundaryValue(value: unknown): string {
  if (value === undefined || value === null) return '';
  return String(value);
}

function boolBoundaryValue(value: unknown): boolean {
  return value === true;
}

function normalizeGender(value: unknown): string {
  const normalized = String(value ?? '').trim().toLowerCase();
  if (normalized === 'm' || normalized === 'male') return 'M';
  if (normalized === 'f' || normalized === 'female') return 'F';
  return '';
}

function getBoundaryValue(
  sourceFilters: Partial<FiltersState>,
  key: keyof FiltersState,
  vacancyValue: unknown,
): unknown {
  return hasOwnValue(sourceFilters as Record<string, unknown>, key)
    ? (sourceFilters as any)[key]
    : vacancyValue;
}

function mapVacancyBoundaryFilters(
  vacancy: Record<string, unknown>,
  sourceFilters: Partial<FiltersState>,
): Partial<FiltersState> {
  const salarySpecified = (vacancy as any).salarySpecified
    ?? (vacancy as any).incomeSpecified
    ?? (vacancy as any).hasSalary;

  return {
    positionName: stringifyBoundaryValue(
      getBoundaryValue(sourceFilters, 'positionName', (vacancy as any).positionName ?? (vacancy as any).title),
    ),
    salaryFrom: stringifyBoundaryValue(
      getBoundaryValue(sourceFilters, 'salaryFrom', (vacancy as any).salaryFrom),
    ),
    salaryTo: stringifyBoundaryValue(
      getBoundaryValue(sourceFilters, 'salaryTo', (vacancy as any).salaryTo),
    ),
    incomeSpecified: boolBoundaryValue(
      getBoundaryValue(sourceFilters, 'incomeSpecified', salarySpecified),
    ),
    hasSalary: boolBoundaryValue(
      getBoundaryValue(sourceFilters, 'hasSalary', salarySpecified),
    ),
    ageFrom: stringifyBoundaryValue(
      getBoundaryValue(sourceFilters, 'ageFrom', (vacancy as any).ageFrom),
    ),
    ageTo: stringifyBoundaryValue(
      getBoundaryValue(sourceFilters, 'ageTo', (vacancy as any).ageTo),
    ),
    gender: normalizeGender(
      getBoundaryValue(sourceFilters, 'gender', (vacancy as any).gender),
    ),
  };
}

export function mapAvitoVacancyToFilters(
  vacancy: Record<string, unknown>,
  filters: Partial<FiltersState> = {},
  sourceFilters: Partial<FiltersState> = {},
): Partial<FiltersState> {
  const avitoLocationIds = normalizeAvitoVacancyList((vacancy as any).avitoLocationIds);
  const experience = mapHhTotalExperienceToAvitoExperience(
    firstFilledValue(sourceFilters.totalExperience, filters.totalExperience, vacancy.totalExperience),
  );
  const education = mapHhEducationToAvitoEducation(
    firstFilledValue(sourceFilters.education, filters.education, vacancy.education),
  );
  const workFormatSchedule = mapHhWorkFormatToAvitoSchedule(
    firstFilledValue(sourceFilters.workFormat, filters.workFormat, vacancy.workFormat),
  );
  const workScheduleSchedule = mapHhWorkScheduleToAvitoSchedule(
    firstFilledValue(
      (sourceFilters as any).workSchedule,
      (filters as any).workSchedule,
      vacancy.workSchedule,
      vacancy.schedule,
    ),
  );
  const workingHoursSchedule = mapHhWorkingHoursToAvitoSchedule(
    firstFilledValue(
      (sourceFilters as any).workingHours,
      (filters as any).workingHours,
      vacancy.workingHours,
    ),
  );
  const schedule = mergeAvitoScheduleMappings(
    workFormatSchedule,
    workScheduleSchedule,
    workingHoursSchedule,
  );
  const avitoKeywords = getRequiredSkillsFromSource(vacancy);

  return {
    ...mapVacancyBoundaryFilters(vacancy, sourceFilters),
    citizenship: [],
    selectedSpecializations: normalizeAvitoVacancyList(vacancy.avitoSpecialization),
    avitoKeywords,
    ...(education.length > 0 ? { avitoEducationLevel: education } : {}),
    avitoSchedule: schedule,
    cityIds: avitoLocationIds,
    cities: [],
    metro: '',
    metroCityId: '',
    ...(experience || {}),
  };
}
